import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { Logo } from "@/constants/brand";

export default function Auth() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [reset, setReset] = useState({ open: false, sent: false, code: "", password: "" });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const closeReset = () => setReset({ open: false, sent: false, code: "", password: "" });
  const sendResetCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot", { email: form.email });
      setReset((r) => ({ ...r, sent: true }));
      toast.success("Αν υπάρχει λογαριασμός με αυτό το email, στάλθηκε κωδικός.");
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  const doReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset", { email: form.email, code: reset.code.trim(), new_password: reset.password });
      toast.success("Ο κωδικός άλλαξε. Συνδεθείτε με τον νέο κωδικό.");
      setForm((f) => ({ ...f, password: "" }));
      closeReset();
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success("Καλώς ήρθατε!");
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-5 py-10" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-[400px] fadeup">
        <div className="flex flex-col items-center text-center mb-8">
          <Logo size={96} className="mb-5" />
          <h1 className="text-[34px] font-bold tracking-tight leading-none">
            e-diki<span style={{ color: "var(--gold)" }}>Μου</span>
          </h1>
          <p className="text-[var(--txt-2)] mt-3 text-[15px] leading-relaxed max-w-[320px]">
            Παρακολούθηση δικασίμων σε πραγματικό χρόνο. Ειδοποίηση μόλις η υπόθεσή σας πλησιάζει στη σειρά της.
          </p>
        </div>

        {reset.open ? (
          <div className="card p-7" data-testid="reset-card">
            <h2 className="text-xl font-semibold tracking-tight mb-1">Επαναφορά κωδικού</h2>
            <p className="text-sm text-[var(--txt-2)] mb-5">
              {reset.sent ? "Πληκτρολογήστε τον κωδικό που λάβατε στο email και τον νέο σας κωδικό."
                : "Θα στείλουμε έναν κωδικό μίας χρήσης στο email του λογαριασμού σας."}
            </p>
            <form onSubmit={reset.sent ? doReset : sendResetCode} className="space-y-4">
              <div>
                <label className="label block mb-1.5">Email</label>
                <input data-testid="reset-email" type="email" required value={form.email} onChange={upd("email")}
                  disabled={reset.sent} className="field w-full px-3.5 py-2.5 text-sm" placeholder="dikigoros@lawfirm.gr" />
              </div>
              {reset.sent && (
                <>
                  <div>
                    <label className="label block mb-1.5">Κωδικός από το email</label>
                    <input data-testid="reset-code" inputMode="numeric" maxLength={6} required value={reset.code}
                      onChange={(e) => setReset((r) => ({ ...r, code: e.target.value.replace(/\D/g, "") }))}
                      className="field w-full px-3.5 py-2.5 text-sm font-mono tracking-widest" placeholder="6 ψηφία" />
                  </div>
                  <div>
                    <label className="label block mb-1.5">Νέος κωδικός (τουλάχιστον 8 χαρακτήρες)</label>
                    <input data-testid="reset-password" type="password" required minLength={8} value={reset.password}
                      onChange={(e) => setReset((r) => ({ ...r, password: e.target.value }))}
                      className="field w-full px-3.5 py-2.5 text-sm" placeholder="••••••••" />
                  </div>
                </>
              )}
              <button data-testid="reset-submit" disabled={loading || (reset.sent && reset.code.length < 6)}
                className="btn-primary w-full py-3 flex items-center justify-center gap-2 text-[15px]">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {reset.sent ? "Αλλαγή κωδικού" : "Αποστολή κωδικού"}
              </button>
              <button type="button" onClick={closeReset} className="w-full text-sm text-[var(--txt-2)] underline">Πίσω στη σύνδεση</button>
            </form>
          </div>
        ) : (
        <div className="card p-7">
          <div className="flex p-1 rounded-full mb-6" style={{ background: "#ECECEF" }}>
            {[["login", "Σύνδεση"], ["register", "Εγγραφή"]].map(([m, lbl]) => (
              <button key={m} data-testid={`tab-${m}`} onClick={() => setMode(m)}
                className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all ${mode === m ? "bg-white shadow-sm" : "text-[var(--txt-2)]"}`}>
                {lbl}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="label block mb-1.5">Ονοματεπώνυμο</label>
                <input data-testid="auth-name-input" required value={form.name} onChange={upd("name")}
                  className="field w-full px-3.5 py-2.5 text-sm" placeholder="Μαρία Παπαδοπούλου" />
              </div>
            )}
            <div>
              <label className="label block mb-1.5">Email</label>
              <input data-testid="auth-email-input" type="email" required value={form.email} onChange={upd("email")}
                className="field w-full px-3.5 py-2.5 text-sm" placeholder="dikigoros@lawfirm.gr" />
            </div>
            <div>
              <label className="label block mb-1.5">Κωδικός</label>
              <input data-testid="auth-password-input" type="password" required minLength={mode === "register" ? 8 : undefined}
                value={form.password} onChange={upd("password")}
                className="field w-full px-3.5 py-2.5 text-sm" placeholder="••••••••" />
              {mode === "register" && <p className="text-[11px] text-[var(--txt-3)] mt-1">Τουλάχιστον 8 χαρακτήρες.</p>}
            </div>
            <button data-testid="auth-submit-btn" disabled={loading}
              className="btn-primary w-full py-3 flex items-center justify-center gap-2 text-[15px]">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Σύνδεση" : "Δημιουργία λογαριασμού"}
            </button>
            {mode === "login" && (
              <button type="button" data-testid="forgot-link" onClick={() => setReset({ open: true, sent: false, code: "", password: "" })}
                className="w-full text-sm text-[var(--txt-2)] underline">Ξεχάσατε τον κωδικό σας;</button>
            )}
          </form>
        </div>
        )}

        <p className="text-center text-xs text-[var(--txt-3)] mt-6 leading-relaxed">
          Τα δεδομένα αντλούνται από το δημόσιο πινάκιο<br />e-diki.justice.gov.gr
        </p>
      </div>
    </div>
  );
}
